import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getTakeFrameById } from "service/api";
import { Card, CardBody, Container, Row, Col } from "reactstrap";
import jsPDF from "jspdf";
import "jspdf-autotable";

const THEME = "#DC4C64";
const API_URL = process.env.REACT_APP_MYAPI_URL || "";

const getLogoUrl = (logo) => {
    if (!logo) return null;
    const filename = logo.split("/").pop();
    return `${API_URL}/uploads/${filename}`;
};

const SectionTitle = ({ children }) => (
    <div style={{
        backgroundColor: THEME, color: "#fff", fontWeight: 700,
        fontSize: 13, padding: "5px 12px", borderRadius: "4px 4px 0 0", marginBottom: 0,
    }}>{children}</div>
);

const Field = ({ label, value }) => (
    <div style={{ marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 12, color: "#777" }}>{label}</span>
        <div style={{ fontSize: 13, color: "#222", fontWeight: 500, marginTop: 1 }}>
            {value || <span style={{ color: "#ccc" }}>—</span>}
        </div>
    </div>
);

const ViewFrametaken = () => {
    const { id } = useParams();
    const [frame, setFrame] = useState(null);
    const [loading, setLoading] = useState(true);
    const printRef = useRef(null);

    useEffect(() => {
        getTakeFrameById(id)
            .then(res => setFrame(res?.data))
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [id]);

    const handlePrint = () => {
        const content = printRef.current;
        const printWindow = window.open("", "_blank");
        const styleEl = printWindow.document.createElement("style");
        styleEl.textContent = `
            body { font-family: Arial, sans-serif; font-size: 13px; color: #222; margin: 20px; }
            h2 { color: #DC4C64; border-bottom: 2px solid #DC4C64; padding-bottom: 6px; }
            .section { margin-bottom: 18px; }
            .section-title { background: #DC4C64; color: #fff; font-weight: 700; padding: 5px 12px; border-radius: 4px 4px 0 0; font-size: 13px; }
            .section-body { border: 1px solid #DC4C64; border-top: none; border-radius: 0 0 6px 6px; padding: 12px 16px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 20px; }
            .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px 16px; }
            .field-label { font-size: 11px; color: #777; font-weight: 600; }
            .field-value { font-size: 13px; font-weight: 500; }
            img.logo { height: 50px; margin-bottom: 10px; }
            img.frame-img { max-height: 160px; border: 2px solid #DC4C64; border-radius: 6px; margin-top: 8px; }
            @media print { body { margin: 10px; } }
        `;
        const titleEl = printWindow.document.createElement("title");
        titleEl.textContent = "Frame Record";
        printWindow.document.head.appendChild(titleEl);
        printWindow.document.head.appendChild(styleEl);
        printWindow.document.body.innerHTML = content.innerHTML;
        printWindow.focus();
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 400);
    };

    const handleDownloadPDF = async () => {
        const doc = new jsPDF();
        const pageW = doc.internal.pageSize.width;
        const margin = 10;
        const contentW = pageW - margin * 2;
        const themeRGB = [220, 76, 100];
        let startY = margin;

        // Load image URL → base64 via fetch (avoids canvas CORS taint)
        const loadImg = async (url) => {
            if (!url) return null;
            try {
                const res = await fetch(url, { credentials: "omit" });
                if (!res.ok) return null;
                const blob = await res.blob();
                const dataUrl = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
                // Get dimensions
                const dims = await new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => resolve({ w: img.width, h: img.height });
                    img.onerror = () => resolve({ w: 100, h: 100 });
                    img.src = dataUrl;
                });
                return { dataUrl, ...dims };
            } catch { return null; }
        };

        const p = frame.visit_id?.patientid;
        const c = frame.visit_id?.patientid?.clinicname || frame.clinic_id;
        const visitNo = frame.visit_no || frame.visit_id?.visit_no;

        // Shared styles
        const headStyles = { fillColor: themeRGB, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9, cellPadding: 3 };
        const lbStyle = { fontStyle: "bold", textColor: [120, 120, 120], fontSize: 7.5, fillColor: [248, 248, 248], cellPadding: 2.5 };
        const valStyle = { fontStyle: "normal", textColor: [25, 25, 25], fontSize: 9, fillColor: [255, 255, 255], cellPadding: 2.5 };
        const tableStyles = { lineColor: [225, 225, 225], lineWidth: 0.15, overflow: "linebreak" };
        const tableOpts = { margin: { left: margin, right: margin }, tableWidth: contentW, tableLineColor: [210, 210, 210], tableLineWidth: 0.2 };

        // Helper: label cell + value cell pair
        const lv = (label, value) => [
            { content: String(label), styles: lbStyle },
            { content: String(value || "—"), styles: valStyle },
        ];

        // ── Header: Logo + Title ──
        const logoUrl = getLogoUrl(c?.logo);
        const logoData = logoUrl ? await loadImg(logoUrl) : null;
        if (logoData) {
            const maxH = 14, maxW = 38;
            const ratio = Math.min(maxW / logoData.w, maxH / logoData.h);
            doc.addImage(logoData.dataUrl, "JPEG", margin, startY, logoData.w * ratio, logoData.h * ratio);
        }
        doc.setFont("helvetica", "bold");
        doc.setFontSize(15);
        doc.setTextColor(...themeRGB);
        doc.text("Frame Record Details", pageW - margin, startY + 6, { align: "right" });
        if (c?.name) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8.5);
            doc.setTextColor(110, 110, 110);
            doc.text(c.name, pageW - margin, startY + 12, { align: "right" });
        }
        startY += 19;
        doc.setDrawColor(...themeRGB);
        doc.setLineWidth(0.5);
        doc.line(margin, startY, pageW - margin, startY);
        startY += 4;

        // ── Patient Details ──
        const lLabel = 28;
        const lVal = contentW / 2 - lLabel;
        const colStyles4 = { 0: { cellWidth: lLabel }, 1: { cellWidth: lVal }, 2: { cellWidth: lLabel }, 3: { cellWidth: lVal } };

        const patientBody = p ? [
            [...lv("Title", p.title),       ...lv("Date of Birth", p.dob)],
            [...lv("First Name", p.firstname), ...lv("Age", p.age?.toString())],
            [...lv("Last Name", p.lastname),  ...lv("Gender", p.gender)],
            [...lv("MPI No.", p.mpino),       ...lv("Contact", p.mobile)],
            [{ content: "ID Number", styles: lbStyle }, { content: String(p.idnumber || "—"), colSpan: 3, styles: valStyle }],
            [{ content: "Address",   styles: lbStyle }, { content: String(p.address   || "—"), colSpan: 3, styles: valStyle }],
        ] : [];
        if (visitNo) patientBody.push([{ content: "Visit No.", styles: lbStyle }, { content: String(visitNo), colSpan: 3, styles: valStyle }]);

        doc.autoTable({
            startY,
            head: [[{ content: "Patient Details", colSpan: 4, styles: headStyles }]],
            body: patientBody,
            columnStyles: colStyles4,
            styles: tableStyles,
            ...tableOpts,
        });
        startY = doc.lastAutoTable.finalY + 4;

        // ── Practice / Clinic ──
        const clinicBody = c ? [
            [...lv("Practice Name", c.name),    ...lv("Address",  c.address)],
            [...lv("Email",         c.email),    ...lv("City",     c.city)],
            [...lv("Contact No.",   c.contactno),...lv("Province", c.province)],
        ] : [];

        doc.autoTable({
            startY,
            head: [[{ content: "Practice / Clinic", colSpan: 4, styles: headStyles }]],
            body: clinicBody,
            columnStyles: colStyles4,
            styles: tableStyles,
            ...tableOpts,
        });
        startY = doc.lastAutoTable.finalY + 4;

        // ── Frame Details ──
        const fLabel = 24;
        const fVal = contentW / 3 - fLabel;
        doc.autoTable({
            startY,
            head: [[{ content: "Frame Details", colSpan: 6, styles: headStyles }]],
            body: [
                [...lv("Brand", frame.frame_brand),    ...lv("Color",    frame.frame_color), ...lv("Size",  frame.frame_size)],
                [...lv("Material", frame.frame_material), ...lv("Type", frame.frame_type),   ...lv("Shape", frame.frame_shape)],
            ],
            columnStyles: {
                0: { cellWidth: fLabel }, 1: { cellWidth: fVal },
                2: { cellWidth: fLabel }, 3: { cellWidth: fVal },
                4: { cellWidth: fLabel }, 5: { cellWidth: fVal },
            },
            styles: tableStyles,
            ...tableOpts,
        });
        startY = doc.lastAutoTable.finalY + 5;

        // ── Frame Photo ──
        if (frame.frame_image) {
            const frameImg = await loadImg(frame.frame_image);
            if (frameImg) {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(8);
                doc.setTextColor(120, 120, 120);
                doc.text("Frame Photo", margin, startY);
                startY += 3;
                const maxH = 40, maxW = contentW * 0.5;
                const ratio = Math.min(maxW / frameImg.w, maxH / frameImg.h);
                const fw = frameImg.w * ratio, fh = frameImg.h * ratio;
                doc.addImage(frameImg.dataUrl, "JPEG", margin, startY, fw, fh);
                doc.setDrawColor(...themeRGB);
                doc.setLineWidth(0.4);
                doc.rect(margin, startY, fw, fh);
            }
        }

        doc.save(`frame-record-${id}.pdf`);
    };

    if (loading) return (
        <div className="page-content">
            <Container fluid>
                <div className="text-center py-5">
                    <span className="spinner-border" style={{ color: THEME }} />
                    <p className="mt-2 text-muted">Loading frame record...</p>
                </div>
            </Container>
        </div>
    );

    if (!frame) return (
        <div className="page-content">
            <Container fluid>
                <div className="text-center py-5 text-muted">Frame record not found.</div>
            </Container>
        </div>
    );

    const patient = frame.visit_id?.patientid;
    const clinic = patient?.clinicname || frame.clinic_id;
    const visit = frame.visit_id;

    return (
        <div className="page-content">
            <Container fluid>

                {/* Top bar */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 style={{ margin: 0, fontWeight: 700 }}>Frame Record Details</h4>
                    <div className="d-flex gap-2">
                        {/* <button className="btn btn-sm btn-outline-secondary" onClick={handlePrint}>
                            <i className="mdi mdi-printer me-1" /> Print
                        </button> */}
                        <button className="btn btn-sm btn-success" onClick={handleDownloadPDF}>
                            <i className="mdi mdi-download me-1" /> Download PDF
                        </button>
                        <Link to="/list-takeframe" className="btn btn-danger btn-sm">
                            <i className="mdi mdi-format-list-bulleted me-1" /> Back to List
                        </Link>
                    </div>
                </div>

                {/* Printable content */}
                <div ref={printRef}>
                    <Row>

                        {/* ── LEFT: Patient + Visit ── */}
                        <Col md={6} className="mb-3">
                            <SectionTitle><i className="mdi mdi-account me-1" /> Patient Details</SectionTitle>
                            <Card style={{ border: `1px solid ${THEME}`, borderTop: "none", borderRadius: "0 0 6px 6px" }}>
                                <CardBody>
                                    {patient ? (
                                        <Row>
                                            <Col md={6}>
                                                <Field label="Title" value={patient.title} />
                                                <Field label="First Name" value={patient.firstname} />
                                                <Field label="Last Name" value={patient.lastname} />
                                                <Field label="MPI No." value={patient.mpino} />
                                            </Col>
                                            <Col md={6}>
                                                <Field label="Date of Birth" value={patient.dob} />
                                                <Field label="Age" value={patient.age} />
                                                <Field label="Gender" value={patient.gender} />
                                                <Field label="Contact" value={patient.mobile} />
                                            </Col>
                                            <Col md={12}>
                                                <Field label="ID Number" value={patient.idnumber} />
                                                <Field label="Address" value={patient.address} />
                                            </Col>
                                        </Row>
                                    ) : (
                                        <p className="text-muted mb-0" style={{ fontSize: 13 }}>No patient linked to this visit.</p>
                                    )}

                                    {(frame.visit_no || visit?.visit_no) && (
                                        <>
                                            <hr style={{ borderColor: "#f0f0f0", margin: "10px 0" }} />
                                            <Field label="Visit No." value={frame.visit_no || visit?.visit_no} />
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        {/* ── RIGHT: Practice / Clinic ── */}
                        <Col md={6} className="mb-3">
                            <SectionTitle><i className="mdi mdi-hospital-building me-1" /> Practice</SectionTitle>
                            <Card style={{ border: `1px solid ${THEME}`, borderTop: "none", borderRadius: "0 0 6px 6px" }}>
                                <CardBody>
                                    {clinic ? (
                                        <Row>
                                            <Col md={12}>
                                                {clinic.logo && (
                                                    <div className="mb-3">
                                                        <img
                                                            src={getLogoUrl(clinic.logo)}
                                                            alt="Clinic logo"
                                                            style={{ height: 60, objectFit: "contain", borderRadius: 4 }}
                                                        />
                                                    </div>
                                                )}
                                            </Col>
                                            <Col md={6}>
                                                <Field label="Practice Name" value={clinic.name} />
                                                <Field label="Email" value={clinic.email} />
                                                <Field label="Contact No." value={clinic.contactno} />
                                            </Col>
                                            <Col md={6}>
                                                <Field label="Address" value={clinic.address} />
                                                <Field label="City" value={clinic.city} />
                                                <Field label="Province" value={clinic.province} />
                                            </Col>
                                        </Row>
                                    ) : (
                                        <p className="text-muted mb-0" style={{ fontSize: 13 }}>No practice information available.</p>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        {/* ── FRAME DETAILS ── */}
                        <Col md={12} className="mb-3">
                            <SectionTitle><i className="mdi mdi-glasses me-1" /> Frame Details</SectionTitle>
                            <Card style={{ border: `1px solid ${THEME}`, borderTop: "none", borderRadius: "0 0 6px 6px" }}>
                                <CardBody>
                                    <Row>
                                        <Col md={2}><Field label="Brand" value={frame.frame_brand} /></Col>
                                        <Col md={2}><Field label="Color" value={frame.frame_color} /></Col>
                                        <Col md={2}><Field label="Size" value={frame.frame_size} /></Col>
                                        <Col md={2}><Field label="Material" value={frame.frame_material} /></Col>
                                        <Col md={2}><Field label="Type" value={frame.frame_type} /></Col>
                                        <Col md={2}><Field label="Shape" value={frame.frame_shape} /></Col>
                                    </Row>

                                    {frame.frame_image && (
                                        <>
                                            <hr style={{ borderColor: "#f0f0f0", margin: "12px 0" }} />
                                            <div>
                                                <span style={{ fontWeight: 600, fontSize: 12, color: "#777" }}>Frame Photo</span>
                                                <div className="mt-2">
                                                    <img
                                                        src={frame.frame_image}
                                                        alt="Frame"
                                                        style={{
                                                            maxHeight: 200, maxWidth: "100%",
                                                            borderRadius: 8, border: `2px solid ${THEME}`,
                                                            objectFit: "contain",
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                    </Row>
                </div>

            </Container>
        </div>
    );
};

export default ViewFrametaken;
